import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, type UserRole } from '@/contexts/AuthContext'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge, Select, Input } from '@/components/ui'
import {
  Shield,
  Users,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  Inbox,
  CheckCircle,
  XCircle,
  Clock,
  Crown,
  User,
  Trash2,
  Activity,
  TrendingUp,
  RefreshCw,
  Link2,
  Plus,
  ExternalLink,
  Copy,
  Check,
  BarChart3,
  SlidersHorizontal,
  Save,
  Eye,
  EyeOff,
  Cookie,
  Upload,
  FileText,
} from 'lucide-react'
import { toast } from 'sonner'

interface Stats {
  users: {
    total: number
    admins: number
    members: number
    users: number
  }
  votes: {
    total: number
    byType: { working: number; not_working: number; needs_update: number }
    byEncryptor: Record<string, { working: number; not_working: number; needs_update: number }>
  }
  requests: {
    total: number
    pending: number
    approved: number
    rejected: number
  }
  recentActivity: Array<{
    type: string
    message: string
    timestamp: string
  }>
}

interface UserData {
  id: string
  username: string
  email: string
  role: UserRole
  createdAt: string
  lastLogin: string
}

interface RequestData {
  id: string
  userId: string
  username: string
  websites: string[]
  gateway: string
  message: string | null
  status: 'pending' | 'approved' | 'rejected'
  timestamp: string
}

interface RedirectData {
  id: string
  slug: string
  url: string
  hits: number
  createdAt: string
}

interface ProxySettings {
  host: string
  port: number
  username: string
  password: string
}

interface SiteSettings {
  proxy: ProxySettings
  ipLookup: { maxPerDay: number }
}

type AdminTab = 'overview' | 'users' | 'votes' | 'requests' | 'redirects' | 'cookies' | 'settings'

export function AdminDashboard() {
  const navigate = useNavigate()
  const { isAdmin, isLoading: authLoading } = useAuth()
  const [activeTab, setActiveTab] = useState<AdminTab>('overview')
  const [stats, setStats] = useState<Stats | null>(null)
  const [users, setUsers] = useState<UserData[]>([])
  const [requests, setRequests] = useState<RequestData[]>([])
  const [redirects, setRedirects] = useState<RedirectData[]>([])
  const [newSlug, setNewSlug] = useState('')
  const [newUrl, setNewUrl] = useState('')
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null)
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    proxy: { host: '', port: 7000, username: '', password: '' },
    ipLookup: { maxPerDay: 30 },
  })
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [hasCookies, setHasCookies] = useState(false)
  const [cookieText, setCookieText] = useState('')
  const [uploadingCookies, setUploadingCookies] = useState(false)
  const cookieFileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/')
      toast.error('Access denied')
    }
  }, [isAdmin, authLoading, navigate])

  const fetchStats = async () => {
    try {
      const res = await fetch('/api/admin/stats', { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setStats(data)
      }
    } catch {
      toast.error('Failed to load stats')
    }
  }

  const fetchUsers = async () => {
    try {
      const res = await fetch('/api/admin/users', { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setUsers(data.users)
      }
    } catch {
      toast.error('Failed to load users')
    }
  }

  const fetchRequests = async () => {
    try {
      const res = await fetch('/api/requests/list', { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setRequests(data.requests)
      }
    } catch {
      toast.error('Failed to load requests')
    }
  }

  const fetchRedirects = async () => {
    try {
      const res = await fetch('/api/redirects/manage', { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setRedirects(data.redirects)
      }
    } catch {
      toast.error('Failed to load redirects')
    }
  }

  const createRedirect = async () => {
    const slug = newSlug.trim()
    const url = newUrl.trim()
    if (!slug || !url) {
      toast.error('Slug and URL are required')
      return
    }
    try {
      const res = await fetch('/api/redirects/manage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ slug, url }),
      })
      const data = await res.json()
      if (res.ok) {
        toast.success(`Redirect /${slug} created`)
        setNewSlug('')
        setNewUrl('')
        fetchRedirects()
      } else {
        toast.error(data.error || 'Failed to create redirect')
      }
    } catch {
      toast.error('Network error')
    }
  }

  const deleteRedirect = async (id: string, slug: string) => {
    if (!confirm(`Delete redirect /${slug}?`)) return
    try {
      const res = await fetch('/api/redirects/manage', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      })
      if (res.ok) {
        toast.success('Redirect deleted')
        fetchRedirects()
      }
    } catch {
      toast.error('Network error')
    }
  }

  const copyRedirectUrl = (slug: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/${slug}`)
    setCopiedSlug(slug)
    setTimeout(() => setCopiedSlug(null), 2000)
  }

  const fetchSettings = async () => {
    try {
      const res = await fetch('/api/admin/settings', { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setSiteSettings({
          proxy: data.proxy || { host: '', port: 7000, username: '', password: '' },
          ipLookup: data.ipLookup || { maxPerDay: 30 },
        })
      }
    } catch {
      // Settings may not exist yet
    }
  }

  const fetchCookieStatus = async () => {
    try {
      const res = await fetch('/api/tools/video-download/cookies/status', { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setHasCookies(data.hasCookies)
      }
    } catch {}
  }

  const uploadCookies = async () => {
    const text = cookieText.trim()
    if (!text) {
      toast.error('Please paste cookie data or upload a file')
      return
    }
    setUploadingCookies(true)
    try {
      const res = await fetch('/api/tools/video-download/cookies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ cookies: text }),
      })
      const data = await res.json()
      if (!res.ok || data.error) {
        toast.error(data.error || 'Failed to upload cookies')
        return
      }
      toast.success(data.message || 'Cookies uploaded!')
      setHasCookies(true)
      setCookieText('')
    } catch {
      toast.error('Failed to upload cookies')
    } finally {
      setUploadingCookies(false)
    }
  }

  const removeCookies = async () => {
    if (!confirm('Remove cookies? Private/age-restricted video downloads will stop working.')) return
    try {
      const res = await fetch('/api/tools/video-download/cookies', {
        method: 'DELETE',
        credentials: 'include',
      })
      if (res.ok) {
        setHasCookies(false)
        toast.success('Cookies removed')
      }
    } catch {
      toast.error('Failed to remove cookies')
    }
  }

  const handleCookieFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setCookieText(reader.result as string)
    reader.readAsText(file)
    e.target.value = ''
  }

  const saveSettings = async () => {
    setSettingsSaving(true)
    try {
      const res = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(siteSettings),
      })
      if (res.ok) {
        toast.success('Settings saved')
      } else {
        toast.error('Failed to save settings')
      }
    } catch {
      toast.error('Network error')
    } finally {
      setSettingsSaving(false)
    }
  }

  const loadAll = async () => {
    setLoading(true)
    await Promise.all([fetchStats(), fetchUsers(), fetchRequests(), fetchRedirects(), fetchSettings(), fetchCookieStatus()])
    setLoading(false)
  }

  useEffect(() => {
    if (isAdmin) {
      loadAll()
    }
  }, [isAdmin])

  const updateUserRole = async (userId: string, role: UserRole) => {
    try {
      const res = await fetch('/api/admin/users', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, role }),
      })
      const data = await res.json()
      if (res.ok) {
        toast.success(`Role updated to ${role}`)
        fetchUsers()
        fetchStats()
      } else {
        toast.error(data.error || 'Failed to update role')
      }
    } catch {
      toast.error('Network error')
    }
  }

  const deleteUser = async (userId: string, username: string) => {
    if (!confirm(`Delete user "${username}"? This cannot be undone.`)) return

    try {
      const res = await fetch('/api/admin/users', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      })
      if (res.ok) {
        toast.success('User deleted')
        fetchUsers()
        fetchStats()
      } else {
        const data = await res.json()
        toast.error(data.error || 'Failed to delete user')
      }
    } catch {
      toast.error('Network error')
    }
  }

  const updateRequestStatus = async (requestId: string, status: 'approved' | 'rejected') => {
    try {
      const res = await fetch('/api/requests/list', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ requestId, status }),
      })
      if (res.ok) {
        toast.success(`Request ${status}`)
        fetchRequests()
        fetchStats()
      }
    } catch {
      toast.error('Network error')
    }
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatRelative = (dateStr: string) => {
    const d = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days}d ago`
    return formatDate(dateStr)
  }

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex items-center gap-3 text-muted-foreground">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          Loading admin panel...
        </div>
      </div>
    )
  }

  if (!isAdmin) return null

  const tabs: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: <Activity className="h-4 w-4" /> },
    { key: 'users', label: 'Users', icon: <Users className="h-4 w-4" /> },
    { key: 'votes', label: 'Votes', icon: <ThumbsUp className="h-4 w-4" /> },
    { key: 'requests', label: 'Requests', icon: <Inbox className="h-4 w-4" /> },
    { key: 'redirects', label: 'Redirects', icon: <Link2 className="h-4 w-4" /> },
    { key: 'cookies', label: 'Cookies', icon: <Cookie className="h-4 w-4" /> },
    { key: 'settings', label: 'Settings', icon: <SlidersHorizontal className="h-4 w-4" /> },
  ]

  const getRoleBadge = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><Crown className="h-3 w-3 mr-1" />Admin</Badge>
      case 'member':
        return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30"><Shield className="h-3 w-3 mr-1" />Member</Badge>
      default:
        return <Badge variant="secondary"><User className="h-3 w-3 mr-1" />User</Badge>
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />Pending</Badge>
      case 'approved':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_registered': return <User className="h-4 w-4 text-blue-400" />
      case 'vote_cast': return <ThumbsUp className="h-4 w-4 text-green-400" />
      case 'request_submitted': return <Inbox className="h-4 w-4 text-purple-400" />
      default: return <Activity className="h-4 w-4 text-muted-foreground" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">
            <span className="text-primary glow-text">{'>'}</span> Admin Panel
          </h1>
          <p className="text-muted-foreground">
            System monitoring and management
          </p>
        </div>
        <Button variant="outline" onClick={loadAll} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-card border border-border rounded-lg p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 flex-1 px-4 py-2.5 text-sm font-medium rounded-md transition-all ${
              activeTab === tab.key
                ? 'bg-primary text-primary-foreground shadow-glow'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div className="space-y-6">
          {/* Stat Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="glass">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Total Users</CardDescription>
                  <Users className="h-5 w-5 text-blue-400" />
                </div>
                <CardTitle className="text-3xl text-primary">{stats.users.total}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 text-xs text-muted-foreground">
                  <span className="text-red-400">{stats.users.admins} admin</span>
                  <span className="text-purple-400">{stats.users.members} member</span>
                  <span>{stats.users.users} user</span>
                </div>
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Total Votes</CardDescription>
                  <TrendingUp className="h-5 w-5 text-green-400" />
                </div>
                <CardTitle className="text-3xl text-primary">{stats.votes.total}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 text-xs text-muted-foreground">
                  <span className="text-green-400">{stats.votes.byType.working} working</span>
                  <span className="text-red-400">{stats.votes.byType.not_working} broken</span>
                  <span className="text-yellow-400">{stats.votes.byType.needs_update} update</span>
                </div>
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Pending Requests</CardDescription>
                  <Clock className="h-5 w-5 text-yellow-400" />
                </div>
                <CardTitle className="text-3xl text-yellow-400">{stats.requests.pending}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 text-xs text-muted-foreground">
                  <span className="text-green-400">{stats.requests.approved} approved</span>
                  <span className="text-red-400">{stats.requests.rejected} rejected</span>
                </div>
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Total Requests</CardDescription>
                  <Inbox className="h-5 w-5 text-purple-400" />
                </div>
                <CardTitle className="text-3xl text-primary">{stats.requests.total}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-muted-foreground">
                  {stats.requests.total > 0
                    ? `${Math.round((stats.requests.approved / stats.requests.total) * 100)}% approval rate`
                    : 'No requests yet'}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Vote Summary */}
            <Card className="glass">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ThumbsUp className="h-5 w-5 text-primary" />
                  <CardTitle>Vote Summary</CardTitle>
                </div>
                <CardDescription>Encryptor status from user votes</CardDescription>
              </CardHeader>
              <CardContent>
                {Object.keys(stats.votes.byEncryptor).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No votes yet</p>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar">
                    {Object.entries(stats.votes.byEncryptor).map(([encId, counts]) => {
                      const total = counts.working + counts.not_working + counts.needs_update
                      const workingPct = total > 0 ? (counts.working / total) * 100 : 0
                      return (
                        <div key={encId} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{encId}</span>
                            <span className="text-xs text-muted-foreground">{total} votes</span>
                          </div>
                          <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                            <div
                              className="bg-green-500 transition-all"
                              style={{ width: `${(counts.working / total) * 100}%` }}
                            />
                            <div
                              className="bg-red-500 transition-all"
                              style={{ width: `${(counts.not_working / total) * 100}%` }}
                            />
                            <div
                              className="bg-yellow-500 transition-all"
                              style={{ width: `${(counts.needs_update / total) * 100}%` }}
                            />
                          </div>
                          <div className="flex gap-3 text-xs text-muted-foreground">
                            <span className="text-green-400">{counts.working} ok</span>
                            <span className="text-red-400">{counts.not_working} broken</span>
                            <span className="text-yellow-400">{counts.needs_update} update</span>
                            <span className="ml-auto">{Math.round(workingPct)}% working</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="glass">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  <CardTitle>Recent Activity</CardTitle>
                </div>
                <CardDescription>Latest system events</CardDescription>
              </CardHeader>
              <CardContent>
                {stats.recentActivity.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No activity yet</p>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar">
                    {stats.recentActivity.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="mt-0.5">{getActivityIcon(item.type)}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{item.message}</p>
                          <p className="text-xs text-muted-foreground">{formatRelative(item.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <CardTitle>User Management</CardTitle>
            </div>
            <CardDescription>{users.length} registered users</CardDescription>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No users yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">User</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Role</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium hidden md:table-cell">Registered</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium hidden lg:table-cell">Last Login</th>
                      <th className="text-right py-3 px-2 text-muted-foreground font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-border/50 hover:bg-accent/30 transition-colors">
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium">{u.username}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          {getRoleBadge(u.role)}
                        </td>
                        <td className="py-3 px-2 text-muted-foreground hidden md:table-cell">
                          {formatDate(u.createdAt)}
                        </td>
                        <td className="py-3 px-2 text-muted-foreground hidden lg:table-cell">
                          {formatRelative(u.lastLogin)}
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex items-center justify-end gap-2">
                            <Select
                              value={u.role}
                              onChange={(e) => updateUserRole(u.id, e.target.value as UserRole)}
                              className="w-28 text-xs"
                            >
                              <option value="user">User</option>
                              <option value="member">Member</option>
                              <option value="admin">Admin</option>
                            </Select>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => deleteUser(u.id, u.username)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Votes Tab */}
      {activeTab === 'votes' && stats && (
        <div className="space-y-6">
          {/* Vote Type Totals */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="glass border-green-500/20">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Working</CardDescription>
                  <ThumbsUp className="h-5 w-5 text-green-400" />
                </div>
                <CardTitle className="text-3xl text-green-400">{stats.votes.byType.working}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="glass border-red-500/20">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Not Working</CardDescription>
                  <ThumbsDown className="h-5 w-5 text-red-400" />
                </div>
                <CardTitle className="text-3xl text-red-400">{stats.votes.byType.not_working}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="glass border-yellow-500/20">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Needs Update</CardDescription>
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                </div>
                <CardTitle className="text-3xl text-yellow-400">{stats.votes.byType.needs_update}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Per-Encryptor Votes */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                <CardTitle>Votes by Encryptor</CardTitle>
              </div>
              <CardDescription>Detailed vote breakdown per encryptor</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.votes.byEncryptor).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No votes cast yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 text-muted-foreground font-medium">Encryptor</th>
                        <th className="text-center py-3 px-2 text-green-400 font-medium">Working</th>
                        <th className="text-center py-3 px-2 text-red-400 font-medium">Broken</th>
                        <th className="text-center py-3 px-2 text-yellow-400 font-medium">Update</th>
                        <th className="text-center py-3 px-2 text-muted-foreground font-medium">Total</th>
                        <th className="text-center py-3 px-2 text-muted-foreground font-medium">Health</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(stats.votes.byEncryptor)
                        .sort(([, a], [, b]) => {
                          const totalA = a.working + a.not_working + a.needs_update
                          const totalB = b.working + b.not_working + b.needs_update
                          return totalB - totalA
                        })
                        .map(([encId, counts]) => {
                          const total = counts.working + counts.not_working + counts.needs_update
                          const healthPct = total > 0 ? Math.round((counts.working / total) * 100) : 0
                          return (
                            <tr key={encId} className="border-b border-border/50 hover:bg-accent/30 transition-colors">
                              <td className="py-3 px-2 font-medium">{encId}</td>
                              <td className="py-3 px-2 text-center text-green-400">{counts.working}</td>
                              <td className="py-3 px-2 text-center text-red-400">{counts.not_working}</td>
                              <td className="py-3 px-2 text-center text-yellow-400">{counts.needs_update}</td>
                              <td className="py-3 px-2 text-center">{total}</td>
                              <td className="py-3 px-2 text-center">
                                <span className={`font-medium ${
                                  healthPct >= 70 ? 'text-green-400' :
                                  healthPct >= 40 ? 'text-yellow-400' :
                                  'text-red-400'
                                }`}>
                                  {healthPct}%
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Redirects Tab */}
      {activeTab === 'redirects' && (
        <div className="space-y-6">
          {/* Add New Redirect */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                <CardTitle>Add Redirect</CardTitle>
              </div>
              <CardDescription>
                Create a short URL that redirects to any external link. Example: /portal → https://t.me/...
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-muted-foreground text-sm shrink-0">/</span>
                  <Input
                    placeholder="slug (e.g. portal)"
                    value={newSlug}
                    onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                    className="flex-1"
                  />
                </div>
                <Input
                  placeholder="Destination URL (e.g. https://t.me/...)"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  className="flex-[2]"
                />
                <Button onClick={createRedirect} className="shrink-0">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Existing Redirects */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary" />
                <CardTitle>Active Redirects</CardTitle>
              </div>
              <CardDescription>{redirects.length} redirect{redirects.length !== 1 ? 's' : ''} configured</CardDescription>
            </CardHeader>
            <CardContent>
              {redirects.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No redirects yet. Add one above.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 text-muted-foreground font-medium">Slug</th>
                        <th className="text-left py-3 px-2 text-muted-foreground font-medium">Destination</th>
                        <th className="text-center py-3 px-2 text-muted-foreground font-medium">
                          <BarChart3 className="h-4 w-4 inline" /> Hits
                        </th>
                        <th className="text-left py-3 px-2 text-muted-foreground font-medium hidden md:table-cell">Created</th>
                        <th className="text-right py-3 px-2 text-muted-foreground font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {redirects.map((r) => (
                        <tr key={r.id} className="border-b border-border/50 hover:bg-accent/30 transition-colors">
                          <td className="py-3 px-2">
                            <span className="font-mono text-primary">/{r.slug}</span>
                          </td>
                          <td className="py-3 px-2">
                            <a
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-foreground flex items-center gap-1 max-w-[300px] truncate"
                            >
                              {r.url}
                              <ExternalLink className="h-3 w-3 shrink-0" />
                            </a>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Badge variant="secondary">{r.hits}</Badge>
                          </td>
                          <td className="py-3 px-2 text-muted-foreground hidden md:table-cell">
                            {formatDate(r.createdAt)}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => copyRedirectUrl(r.slug)}
                                title="Copy full URL"
                              >
                                {copiedSlug === r.slug ? (
                                  <Check className="h-4 w-4 text-green-400" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => deleteRedirect(r.id, r.slug)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Inbox className="h-5 w-5 text-primary" />
              <CardTitle>Encryptor Requests</CardTitle>
            </div>
            <CardDescription>{requests.length} total requests</CardDescription>
          </CardHeader>
          <CardContent>
            {requests.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No requests submitted yet</p>
            ) : (
              <div className="space-y-4">
                {requests.map((req) => (
                  <div
                    key={req.id}
                    className="border border-border rounded-lg p-4 space-y-3 hover:border-primary/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{req.gateway}</span>
                          {getStatusBadge(req.status)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          by {req.username} &middot; {formatRelative(req.timestamp)}
                        </p>
                      </div>
                      {req.status === 'pending' && (
                        <div className="flex gap-2 shrink-0">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => updateRequestStatus(req.id, 'approved')}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            onClick={() => updateRequestStatus(req.id, 'rejected')}
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground font-medium">Websites:</p>
                      <div className="flex flex-wrap gap-1">
                        {req.websites.map((url, i) => (
                          <span key={i} className="text-xs bg-muted px-2 py-1 rounded font-mono">
                            {url}
                          </span>
                        ))}
                      </div>
                    </div>

                    {req.message && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground font-medium">Message:</p>
                        <p className="text-sm text-muted-foreground bg-muted/50 rounded p-2">
                          {req.message}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cookies Tab */}
      {activeTab === 'cookies' && (
        <div className="space-y-6">
          {/* Status Card */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Cookie className="h-5 w-5 text-primary" />
                  <CardTitle>Video Downloader Cookies</CardTitle>
                </div>
                <Badge className={hasCookies
                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                  : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                }>
                  {hasCookies ? 'Active' : 'Not Set'}
                </Badge>
              </div>
              <CardDescription>
                Upload browser cookies to enable downloading private, age-restricted, and sign-in required videos from YouTube and other platforms.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {hasCookies && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-400">Cookies are active. Private and age-restricted video downloads are enabled.</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 shrink-0" onClick={removeCookies}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Remove
                  </Button>
                </div>
              )}

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Cookie Data (Netscape format)</label>
                  <textarea
                    value={cookieText}
                    onChange={e => setCookieText(e.target.value)}
                    placeholder={"# Netscape HTTP Cookie File\n# Paste your cookies.txt content here...\n\n.youtube.com\tTRUE\t/\tTRUE\t0\tSID\tvalue..."}
                    className="w-full h-48 px-3 py-2 text-xs font-mono bg-background border border-border rounded-md resize-y focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div className="flex gap-2">
                  <input
                    ref={cookieFileRef}
                    type="file"
                    accept=".txt,.cookie,.cookies"
                    className="hidden"
                    onChange={handleCookieFile}
                  />
                  <Button variant="outline" onClick={() => cookieFileRef.current?.click()}>
                    <FileText className="h-4 w-4 mr-1.5" />
                    Upload cookies.txt File
                  </Button>
                  <Button onClick={uploadCookies} disabled={uploadingCookies || !cookieText.trim()}>
                    {uploadingCookies ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent mr-1.5" />
                    ) : (
                      <Upload className="h-4 w-4 mr-1.5" />
                    )}
                    {uploadingCookies ? 'Uploading...' : 'Save Cookies'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-base">How to Get Cookies</CardTitle>
              <CardDescription>Step-by-step guide to export browser cookies</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
                <li>Install a cookies export extension in your browser:
                  <div className="mt-1 ml-5 space-y-1">
                    <p className="text-xs"><span className="text-primary">Chrome:</span> "Get cookies.txt LOCALLY" or "EditThisCookie"</p>
                    <p className="text-xs"><span className="text-primary">Firefox:</span> "cookies.txt" by Lennon Hill</p>
                  </div>
                </li>
                <li>Go to <span className="text-primary font-mono">youtube.com</span> (or the target site) and <strong className="text-foreground">log in</strong> with your account</li>
                <li>Click the cookie extension icon and <strong className="text-foreground">export/download</strong> cookies in <span className="text-primary">Netscape format</span></li>
                <li>Upload the exported <span className="font-mono text-primary">cookies.txt</span> file above or paste its contents</li>
              </ol>

              <div className="p-3 rounded-md bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-xs text-yellow-400 font-medium">Important Notes:</p>
                <ul className="mt-1 space-y-1 text-xs text-muted-foreground list-disc list-inside">
                  <li>Cookies may expire — you may need to re-upload them periodically</li>
                  <li>Only upload cookies from accounts you own</li>
                  <li>For YouTube, a logged-in Google account bypasses age gates and some restrictions</li>
                  <li>Cookies are stored server-side and used for all video download requests</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Proxy Settings */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-5 w-5 text-primary" />
                <CardTitle>Proxy Configuration</CardTitle>
              </div>
              <CardDescription>
                Configure the proxy server used for IP lookups and other outgoing API requests.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Host</label>
                  <Input
                    value={siteSettings.proxy.host}
                    onChange={e => setSiteSettings(s => ({ ...s, proxy: { ...s.proxy, host: e.target.value } }))}
                    placeholder="gate.decodo.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Port</label>
                  <Input
                    type="number"
                    value={siteSettings.proxy.port}
                    onChange={e => setSiteSettings(s => ({ ...s, proxy: { ...s.proxy, port: parseInt(e.target.value) || 0 } }))}
                    placeholder="7000"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Username</label>
                  <Input
                    value={siteSettings.proxy.username}
                    onChange={e => setSiteSettings(s => ({ ...s, proxy: { ...s.proxy, username: e.target.value } }))}
                    placeholder="username"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Password</label>
                  <ProxyPasswordInput
                    value={siteSettings.proxy.password}
                    onChange={val => setSiteSettings(s => ({ ...s, proxy: { ...s.proxy, password: val } }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rate Limiting */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                <CardTitle>Rate Limiting</CardTitle>
              </div>
              <CardDescription>
                Configure per-user daily limits for tools. Admins bypass all limits.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">IP Lookups (per user/day)</label>
                  <Input
                    type="number"
                    value={siteSettings.ipLookup.maxPerDay}
                    onChange={e => setSiteSettings(s => ({ ...s, ipLookup: { ...s.ipLookup, maxPerDay: parseInt(e.target.value) || 1 } }))}
                    min={1}
                    max={1000}
                  />
                  <p className="text-xs text-muted-foreground">Maximum IP lookups each user can perform per day</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveSettings} disabled={settingsSaving} className="w-full sm:w-auto">
            <Save className="h-4 w-4 mr-1.5" />
            {settingsSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      )}
    </div>
  )
}

function ProxyPasswordInput({ value, onChange }: { value: string; onChange: (val: string) => void }) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="password"
        className="pr-9"
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}
